import React, { useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import "./InstantServiceDetail.css";
import {
  getFacebookLink,
  getLinkedInLink,
  getWhatsAppLink,
  useVisitorData,
} from "../../utils";
import TestimonialsSection from "../../common/testimonials-section";
import {
  fetchInstantServiceDetailRequest,
  logInstantServiceActivityRequest,
  clearInstantServiceDetail,
} from "../../store/slices/instantServiceSlice";

// --- 2. Main Component ---
const InstantServiceDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const hasLoggedActivity = useRef(false);

  // Get data from Redux store
  const service = useSelector((state) => state.instantService.service);
  const isLoading = useSelector((state) => state.instantService.isLoading);
  const error = useSelector((state) => state.instantService.error);
  const activityLogged = useSelector(
    (state) => state.instantService.activityLogged,
  );

  // Call our custom hook
  const visitorData = useVisitorData();

  const STORAGE_URL = `${process.env.REACT_APP_URL}/storage/`;

  // Fetch Service Details
  useEffect(() => {
    if (id) {
      dispatch(fetchInstantServiceDetailRequest({ id }));
    }

    // Cleanup on unmount
    return () => {
      dispatch(clearInstantServiceDetail());
    };
  }, [dispatch, id]);

  useEffect(() => {
    // Only run if we have the visitor data AND the ID AND activity hasn't been logged
    if (visitorData && id && !hasLoggedActivity.current && !activityLogged) {
      hasLoggedActivity.current = true;
      dispatch(logInstantServiceActivityRequest({ id, visitorData }));
    }
  }, [dispatch, visitorData, id, activityLogged]);
  // -----------------------------

  const formatCurrency = (amount) => {
    if (!amount) return "N/A";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="srv-detail-loader">
        <div className="srv-spinner"></div>
        <p>Loading Details...</p>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="srv-detail-error">
        <h2>Oops!</h2>
        <p>{error || "Something went wrong."}</p>
        <Link to="/" className="srv-back-btn">
          Go Back
        </Link>
      </div>
    );
  }

  const currentUrl = window.location.href;
  const cleanDescription = service.decription
    ? service.decription.replace(/(<([^>]+)>)/gi, "").substring(0, 160)
    : "Check out this financial service tailored for you.";
  const secureImageUrl = service.item_image_url
    ? service.item_image_url.replace(/^http:\/\//i, "https://")
    : "";

  return (
    <div className="srv-detail-container">
      <Helmet>
        <title>{service.title || "Service Details"} | Loan House</title>
        <meta name="description" content={cleanDescription} />
        <link
          rel="icon"
          href={
            service.bank?.bank_logo
              ? `${STORAGE_URL}${service?.bank.bank_logo}`
              : "/favicon.ico"
          }
        />

        <meta property="og:site_name" content="Loan House" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={service?.title} />
        <meta property="og:description" content={cleanDescription} />
        <meta property="og:image" content={service?.item_image_url} />
        <meta property="og:image:secure_url" content={secureImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={service?.title} />
        <meta name="twitter:description" content={cleanDescription} />
        <meta name="twitter:image" content={service?.item_image_url} />
      </Helmet>

      <nav className="srv-detail-nav"></nav>

      <div className="srv-hero-card">
        {service.item_image_url && (
          <div className="srv-hero-image-wrap">
            <img
              src={service.item_image_url}
              alt={service.title || "Service"}
              className="srv-hero-image"
            />
          </div>
        )}

        <div className="srv-hero-content">
          {(service.type || service.code) && (
            <div className="srv-badges">
              {service.type && (
                <span className="srv-badge type-badge">
                  {service.type.replace("_", " ").toUpperCase()}
                </span>
              )}
            </div>
          )}

          {service.title && <h1 className="srv-title">{service.title}</h1>}

          {service.bank && service.bank.bank_name && (
            <div className="srv-bank-info">
              {service.bank.bank_logo && (
                <img
                  src={`${STORAGE_URL}${service.bank.bank_logo}`}
                  alt={service.bank.bank_name}
                  className="srv-bank-logo"
                />
              )}
              <span className="srv-bank-name">
                Provided by <strong>{service.bank.bank_name}</strong>
              </span>
            </div>
          )}

          {(service.whatsup_link ||
            service.facebook_link ||
            service.linkedin_link ||
            service.twitter_link) && (
            <div className="srv-social-share">
              <span>Share :</span>

              {service.whatsup_link && (
                <a
                  href={getWhatsAppLink(service)}
                  target="_blank"
                  rel="noreferrer"
                  className="share-icon wa"
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                  </svg>
                  WhatsApp
                </a>
              )}

              {service.facebook_link && (
                <a
                  href={getFacebookLink(service)}
                  target="_blank"
                  rel="noreferrer"
                  className="share-icon fb"
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Facebook
                </a>
              )}

              {service.linkedin_link && (
                <a
                  href={getLinkedInLink(service)}
                  target="_blank"
                  rel="noreferrer"
                  className="share-icon in"
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                </a>
              )}
            </div>
          )}

          {service.apply_link && (
            <a
              href={service.apply_link}
              target="_blank"
              rel="noreferrer"
              className="srv-apply-btn-large"
            >
              Apply Now
            </a>
          )}
        </div>
      </div>

      {(service.min_amt ||
        service.max_amt ||
        service.min_interest_rate ||
        service.max_interest_rate) && (
        <div className="srv-stats-grid">
          {service.min_amt && (
            <div className="srv-stat-box">
              <span className="stat-label">Min Loan Amount</span>
              <span className="stat-value">
                {formatCurrency(service.min_amt)}
              </span>
            </div>
          )}

          {service.max_amt && (
            <div className="srv-stat-box">
              <span className="stat-label">Max Loan Amount</span>
              <span className="stat-value">
                {formatCurrency(service.max_amt)}
              </span>
            </div>
          )}

          {(service.min_interest_rate || service.max_interest_rate) && (
            <div className="srv-stat-box highlight-stat">
              <span className="stat-label">Interest Rate</span>
              <span className="stat-value">
                {service.min_interest_rate
                  ? `${service.min_interest_rate}%`
                  : ""}
                {service.min_interest_rate && service.max_interest_rate
                  ? " - "
                  : ""}
                {service.max_interest_rate
                  ? `${service.max_interest_rate}%`
                  : ""}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="srv-content-grid">
        {service?.decription && (
          <div className="srv-content-card">
            <h3>Important Details</h3>
            <div className="srv-pre-line-text">{service?.decription}</div>
          </div>
        )}

        {service?.eligibility_criteria && (
          <div className="srv-content-card">
            <h3>Eligibility Criteria</h3>
            <div
              className="srv-html-content"
              dangerouslySetInnerHTML={{
                __html: service?.eligibility_criteria,
              }}
            />
          </div>
        )}

        {service?.require_documents && (
          <div className="srv-content-card">
            <h3>Required Documents</h3>
            <div
              className="srv-html-content"
              dangerouslySetInnerHTML={{ __html: service?.require_documents }}
            />
          </div>
        )}
      </div>

      {/* <TestimonialsSection /> */}
    </div>
  );
};

export default InstantServiceDetail;
